import { API_BASE_URL, API_ORIGIN_BASE_URL } from "../config/apiBaseUrl";

const TOKEN_STORAGE_KEY = "mysipur_auth_token";

type StoryImageFields = {
  editedImageUrl?: string;
  editedImageStorageKey?: string;
  uploadedImageUrl?: string;
  uploadedImageStorageKey?: string;
  imageUrl?: string;
  imageStorageKey?: string;
};

const cache = new Map<string, { url: string; expiresAtMs: number }>();

export function clearPresignCache(): void {
  cache.clear();
}

function apiOrigin(): string {
  return API_ORIGIN_BASE_URL;
}

const R2_MIDDLE_SEGMENTS =
  "(uploads|generated|edited|uploaded-images|generated-images|edited-images)";

/** True for private R2 keys: legacy `{mongoId}/segment/file` or `{email}/segment/file`. */
export function isLikelyR2StorageKey(s: string): boolean {
  if (!s || typeof s !== "string" || s.includes("..")) return false;
  const m = s.match(new RegExp(`^([^/]+)/${R2_MIDDLE_SEGMENTS}/[^/]+$`, "i"));
  if (!m) return false;
  const prefix = m[1];
  if (/^[a-fA-F0-9]{24}$/.test(prefix)) return true;
  if (prefix.includes("@") && !prefix.includes("/") && !prefix.includes("\\")) return true;
  return false;
}

function resolveSyncUrl(url: string | undefined): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url) || url.startsWith("blob:") || url.startsWith("data:")) return url;
  if (url.startsWith("/")) return `${apiOrigin()}${url}`;
  return `${apiOrigin()}/${url}`;
}

/** True when the URL is likely an AWS SigV4 presigned GET (mutating the query breaks the signature). */
export function isPresignedHttpUrl(url: string): boolean {
  return /^https?:\/\//i.test(url) && /[?&]X-Amz-Algorithm=/i.test(url);
}

/** Append cache-bust query only when it will not invalidate a presigned URL. */
export function appendCacheBust(url: string, refreshKey: number): string {
  if (!url) return url;
  if (url.startsWith("blob:") || url.startsWith("data:")) return url;
  if (isPresignedHttpUrl(url)) return url;
  const separator = url.includes("?") ? "&" : "?";
  return `${url}${separator}t=${refreshKey}`;
}

/**
 * Presigned GET; caches until 1 minute before server expiry.
 */
export async function getPresignedImageUrl(
  key: string,
  token: string | null | undefined
): Promise<string | null> {
  if (!key || !isLikelyR2StorageKey(key)) return null;

  const t = token ?? (typeof sessionStorage !== "undefined" ? sessionStorage.getItem(TOKEN_STORAGE_KEY) : null);
  if (!t) return null;

  const now = Date.now();
  const hit = cache.get(key);
  if (hit && hit.expiresAtMs > now + 60_000) {
    return hit.url;
  }

  const res = await fetch(
    `${API_BASE_URL}/images/presign?key=${encodeURIComponent(key)}`,
    { headers: { Authorization: `Bearer ${t}` } }
  );
  if (!res.ok) return null;

  const body = await res.json().catch(() => null);
  const url = body?.data?.url as string | undefined;
  const expiresAt = body?.data?.expiresAt as string | undefined;
  if (!url) return null;

  const expiresAtMs = expiresAt ? new Date(expiresAt).getTime() : now + 3_500_000;
  cache.set(key, { url, expiresAtMs });
  return url;
}

/** Full priority: edited (url/key) → uploaded → generated (DALL·E url or key). */
export async function resolveStoryImageUrl(
  img: StoryImageFields | null | undefined,
  token: string | null | undefined
): Promise<string | null> {
  if (!img) return null;

  // Prefer storage keys first to avoid stale legacy /uploads URLs.
  let u = await getPresignedImageUrl(img.editedImageStorageKey || "", token);
  if (u) return u;
  u = resolveSyncUrl(img.editedImageUrl);
  if (u) return u;

  u = await getPresignedImageUrl(img.uploadedImageStorageKey || "", token);
  if (u) return u;
  u = resolveSyncUrl(img.uploadedImageUrl);
  if (u) return u;

  u = await getPresignedImageUrl(img.imageStorageKey || "", token);
  if (u) return u;
  u = resolveSyncUrl(img.imageUrl);
  return u;
}

/** For editor "original" (skip edited overlay): uploaded then generated only. */
export async function resolveStoryImageUrlOriginal(
  img: StoryImageFields | null | undefined,
  token: string | null | undefined
): Promise<string | null> {
  if (!img) return null;

  let u = await getPresignedImageUrl(img.uploadedImageStorageKey || "", token);
  if (u) return u;
  u = resolveSyncUrl(img.uploadedImageUrl);
  if (u) return u;

  u = await getPresignedImageUrl(img.imageStorageKey || "", token);
  if (u) return u;
  u = resolveSyncUrl(img.imageUrl);
  return u;
}

export async function resolveCoverImageUrl(
  cover: StoryImageFields | null | undefined,
  token: string | null | undefined
): Promise<string | null> {
  return resolveStoryImageUrl(cover, token);
}

export async function resolveReferencePhotoUrl(
  img: {
    storageKey?: string;
    publicUrl?: string;
    path?: string;
    filename?: string;
  } | null,
  token: string | null | undefined
): Promise<string | null> {
  if (!img) return null;
  if (img.publicUrl && /^https?:\/\//i.test(img.publicUrl)) return img.publicUrl;
  if (img.storageKey) {
    const p = await getPresignedImageUrl(img.storageKey, token);
    // Never fall back to /uploads/ for R2 keys — there is no local file.
    return p ?? null;
  }
  if (img.path) {
    const normalized = String(img.path).replace(/\\/g, "/");
    const idx = normalized.toLowerCase().lastIndexOf("/uploads/");
    const file = idx >= 0 ? normalized.slice(idx + "/uploads/".length) : img.filename;
    if (file) return `${apiOrigin()}/uploads/${file}`;
  }
  return null;
}
